import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { 
  fetchExternalIntegrations, 
  fetchStaffExternalIntegrations,
  fetchSellersByAdminThunk,
  createExternalIntegrationThunk, 
  updateExternalIntegrationThunk, 
  deleteExternalIntegrationThunk 
} from './externalIntegrationsThunks';

const initialState = {
  integrations: [],
  sellers: [],
  totalCount: 0,
  loading: false,
  error: null,
  filters: {
    page: 1,
    rowsPerPage: 20,
    search: '',
    orderBy: 'integration_name',
    order: 'asc'
  }
};

const externalIntegrationsSlice = createSlice({
  name: 'externalIntegrations',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setSorting: (state, action) => {
      state.filters.orderBy = action.payload.orderBy;
      state.filters.order = action.payload.order;
      state.filters.page = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Integrations
      .addCase(fetchExternalIntegrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExternalIntegrations.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchExternalIntegrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // The same table read as the internal team, into the same fields.
      .addCase(fetchStaffExternalIntegrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffExternalIntegrations.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchStaffExternalIntegrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Sellers
      .addCase(fetchSellersByAdminThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchSellersByAdminThunk.fulfilled, (state, action) => {
        state.sellers = action.payload;
      })
      .addCase(fetchSellersByAdminThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Create
      .addCase(createExternalIntegrationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExternalIntegrationThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createExternalIntegrationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateExternalIntegrationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExternalIntegrationThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateExternalIntegrationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteExternalIntegrationThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExternalIntegrationThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteExternalIntegrationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, setPage, setRowsPerPage, setSearchFilter, setSorting } = externalIntegrationsSlice.actions;

const persistConfig = {
  key: 'externalIntegrationsFilter',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, externalIntegrationsSlice.reducer);
